import {Injectable} from '@angular/core';

/**
 * Story 54.4 (architecture D32) — the ALTCHA proof-of-work, run invisibly at
 * submit. The widget is the `altcha/external` build (no bundled workers, no
 * blob: worker): it is loaded on first use (code-split) and its PBKDF2 worker
 * is the same-origin file `angular.json` copies from the pinned package, so the
 * page's CSP needs only `worker-src 'self'`.
 *
 * The caller hands in a challenge it fetched for THIS submit; the solver never
 * fetches or caches one (a challenge is single-use and expires in 120 s).
 *
 * A copy of emjo-login's solver: the two apps share no library today.
 */
export const ALTCHA_WORKER_PATH = 'altcha/pbkdf2.js';
const SOLVE_TIMEOUT_MS = 30_000;

@Injectable({providedIn: 'root'})
export class AltchaSolver {
  private loadPromise: Promise<void> | null = null;

  async solve(challenge: unknown): Promise<string> {
    await this.load();
    const el = document.createElement('altcha-widget');
    el.style.display = 'none';
    document.body.appendChild(el);
    try {
      await whenMounted(el);
      await el.configure({
        challenge,
        display: 'invisible',
        minDuration: 0,
        humanInteractionSignature: false,
        timeout: SOLVE_TIMEOUT_MS,
      });
      const result = await el.verify();
      if (!result?.payload) {
        throw new Error('ALTCHA proof-of-work failed');
      }
      return result.payload;
    } finally {
      el.remove();
    }
  }

  /**
   * Loads the widget (once). Callers start it alongside their challenge fetch
   * so a cold first download does not run down the challenge's 120 s TTL.
   */
  load(): Promise<void> {
    this.loadPromise ??= import('altcha/external').then(() => {
      globalThis.$altcha.algorithms.set('PBKDF2/SHA-256',
        () => new Worker(new URL(ALTCHA_WORKER_PATH, document.baseURI)));
    }).catch(err => {
      this.loadPromise = null;
      throw err;
    });
    return this.loadPromise;
  }
}

// The widget is a Svelte custom element: its methods exist only once it has
// mounted, a tick after it is connected.
async function whenMounted(el: AltchaWidgetElement): Promise<void> {
  for (let i = 0; i < 50 && typeof el.verify !== 'function'; i++) {
    await new Promise(resolve => setTimeout(resolve, 10));
  }
  if (typeof el.verify !== 'function') {
    throw new Error('ALTCHA widget did not initialise');
  }
}
